/**
 * Every error code the API can return, in both languages.
 *
 * The server already sends a translated `message`, and this dashboard ignores
 * it. Branching on translated prose breaks the moment the locale changes, and
 * the API's own fallback locale is Arabic — so an English-only client reading
 * `message` would be showing Arabic sentences in an English UI. The code is the
 * contract; this file is the only place it becomes words.
 *
 * `ALL_ERROR_CODES` from @diora/contracts is checked against these keys at
 * startup in development, so a code added to the API without a translation here
 * announces itself immediately rather than reaching a person as
 * SHOUTING_SNAKE_CASE.
 */

import type { Pair } from './pair';

export const errorStrings = {
  // -- transport -----------------------------------------------------------
  BAD_REQUEST: { ar: 'طلب غير صالح.', en: 'The request was not valid.' },
  UNAUTHENTICATED: { ar: 'انتهت جلستك. سجّل الدخول من جديد.', en: 'Your session ended. Sign in again.' },
  FORBIDDEN: { ar: 'لا تملك صلاحية لهذا الإجراء.', en: 'You do not have permission for this.' },
  NOT_FOUND: { ar: 'لم يعد هذا العنصر موجوداً.', en: 'That item no longer exists.' },
  CONFLICT: { ar: 'تعارض مع الحالة الحالية.', en: 'That conflicts with the current state.' },
  PAYLOAD_TOO_LARGE: { ar: 'الملف أكبر من الحد المسموح.', en: 'The file is larger than the limit.' },
  UNSUPPORTED_MEDIA_TYPE: { ar: 'نوع الملف غير مدعوم.', en: 'That file type is not supported.' },
  RATE_LIMITED: { ar: 'محاولات كثيرة. انتظر قليلاً ثم أعد المحاولة.', en: 'Too many attempts. Wait a moment and try again.' },
  INTERNAL_ERROR: { ar: 'خطأ في الخادم. لم يُحفظ أي تغيير.', en: 'Server error. Nothing was saved.' },
  SERVICE_UNAVAILABLE: { ar: 'الخدمة غير متاحة حالياً.', en: 'The service is unavailable right now.' },
  VALIDATION_ERROR: { ar: 'راجع الحقول المميّزة بالأسفل.', en: 'Check the highlighted fields below.' },

  // -- auth and users ------------------------------------------------------
  ACCOUNT_DISABLED: { ar: 'هذا الحساب معطّل. راجع المالك.', en: 'This account is deactivated. Ask the owner.' },
  CANNOT_DEACTIVATE_SELF: { ar: 'لا يمكنك تعطيل حسابك بنفسك.', en: 'You cannot deactivate your own account.' },
  CANNOT_DEMOTE_SELF: { ar: 'لا يمكنك خفض صلاحية حسابك بنفسك.', en: 'You cannot lower your own role.' },
  EMAIL_TAKEN: { ar: 'هذا البريد مستخدم لحساب آخر.', en: 'That email already belongs to another account.' },
  INVALID_CREDENTIALS: { ar: 'البريد أو كلمة المرور غير صحيحة.', en: 'That email or password is not right.' },
  INVALID_REFRESH_TOKEN: { ar: 'انتهت جلستك. سجّل الدخول من جديد.', en: 'Your session ended. Sign in again.' },
  LAST_OWNER: { ar: 'لا بد من بقاء مالك واحد فعّال على الأقل.', en: 'At least one active owner has to remain.' },
  NO_REFRESH_TOKEN: { ar: 'لا توجد جلسة. سجّل الدخول.', en: 'No session. Please sign in.' },
  PASSWORD_CHANGE_REQUIRED: { ar: 'كلمة مرورك مؤقتة. غيّرها للمتابعة.', en: 'Your password is temporary. Change it to continue.' },
  PASSWORD_UNCHANGED: { ar: 'كلمة المرور الجديدة مطابقة للحالية.', en: 'The new password is the same as the current one.' },
  REFRESH_TOKEN_EXPIRED: { ar: 'انتهت صلاحية جلستك.', en: 'Your session expired.' },
  REFRESH_TOKEN_REUSED: {
    ar: 'أُنهيت جميع الجلسات لأسباب أمنية. سجّل الدخول من جديد.',
    en: 'All sessions were ended as a precaution. Sign in again.',
  },

  // -- concurrency ---------------------------------------------------------
  VERSION_CONFLICT: {
    ar: 'حفظ شخص آخر تعديلاً على هذا السجل قبلك. أعد التحميل لرؤية نسخته.',
    en: 'Someone else saved a change to this record first. Reload to see their version.',
  },
  STALE_CONTENT_REVISION: {
    ar: 'تغيّرت القائمة منذ فتحها. أعد التحميل ثم رتّبها من جديد.',
    en: 'The list changed since you opened it. Reload, then reorder again.',
  },
  CONTENT_REVISION_INVALID: { ar: 'مرجع الترتيب غير صالح.', en: 'The reorder reference was not valid.' },

  // -- publication and lifecycle -------------------------------------------
  CATEGORY_ARCHIVED: { ar: 'الفئة المختارة مؤرشفة.', en: 'That category is archived.' },
  CATEGORY_NOT_FOUND: { ar: 'الفئة غير موجودة.', en: 'That category does not exist.' },
  GROUP_ARCHIVED: { ar: 'مجموعة الإضافات مؤرشفة.', en: 'That add-on group is archived.' },
  GROUP_NOT_FOUND: { ar: 'مجموعة الإضافات غير موجودة.', en: 'That add-on group does not exist.' },
  HAS_DEPENDENTS: { ar: 'هذا العنصر مستخدم في مواضع أخرى.', en: 'Other records still use this.' },
  NOT_ARCHIVED: { ar: 'هذا السجل ليس مؤرشفاً.', en: 'That record is not archived.' },
  NOT_PUBLISHABLE: { ar: 'ينقص هذا السجل ما يلزم للنشر.', en: 'This record is still missing what publishing requires.' },
  WOULD_UNPUBLISH: {
    ar: 'هذا التعديل يترك سجلاً منشوراً بحالة لا يمكن نشرها. ألغِ النشر أولاً إن كان مقصوداً.',
    en: 'That edit would leave a published record in a state it could not be published in. Unpublish first if you mean it.',
  },
  TYPE_CHANGE_CLEARS_ITEMS: {
    ar: 'تغيير نوع العرض يزيل الأصناف المرتبطة به.',
    en: 'Changing the offer type removes the items attached to it.',
  },

  // -- lists ---------------------------------------------------------------
  DUPLICATE_IDS: { ar: 'القائمة تحتوي على عنصر مكرر.', en: 'The list repeats an entry.' },
  DUPLICATE_ITEMS: { ar: 'الصنف نفسه مضاف أكثر من مرة.', en: 'The same item is added more than once.' },
  INCOMPLETE_LIST: {
    ar: 'الترتيب يجب أن يشمل كل العناصر غير المؤرشفة.',
    en: 'A reorder has to include every non-archived entry.',
  },
  UNKNOWN_IDS: { ar: 'القائمة تشير إلى عنصر غير موجود.', en: 'The list points at something that does not exist.' },
  UNKNOWN_ITEMS: { ar: 'أحد الأصناف المختارة غير موجود.', en: 'One of the chosen items does not exist.' },
  TOO_MANY_FEATURED: { ar: 'الحد الأقصى ثمانية أصناف مميّزة.', en: 'Eight featured items is the maximum.' },

  // -- offers and pricing ---------------------------------------------------
  ANNOUNCEMENT_HAS_NO_PRICES: {
    ar: 'عرض من نوع إعلان لا يحمل أسعاراً.',
    en: 'An announcement offer does not carry prices.',
  },
  MENU_NOT_CONFIGURED: {
    ar: 'لم تُحدَّد العملة بعد، ولا يمكن نشر أي سعر قبل ذلك.',
    en: 'No currency is set yet, and nothing priced can be published until it is.',
  },
  OFFER_OVERLAP: {
    ar: 'يتداخل هذا العرض مع عرض آخر على الصنف نفسه.',
    en: 'This offer overlaps another one on the same item.',
  },
  PRICE_BREAKS_OFFERS: {
    ar: 'السعر الجديد أقل من سعر خصم فعّال، فيظهر التوفير كزيادة.',
    en: 'That price is below a live discount, so the saving would read as a markup.',
  },
  INVALID_CURRENCY: { ar: 'رمز العملة غير صالح.', en: 'That is not a valid currency code.' },
  CURRENCY_CHANGE_NEEDS_CONFIRMATION: {
    ar: 'تغيير العملة لا يحوّل أي سعر — كل رقم يبقى كما هو ويتغيّر معناه.',
    en: 'Changing the currency converts nothing — every number stays and changes meaning.',
  },

  // -- branches, hours and closures ----------------------------------------
  BRANCH_NOT_PUBLISHED: { ar: 'الفرع غير منشور.', en: 'That branch is not published.' },
  CLOSURE_OVERLAP: {
    ar: 'يتداخل هذا الإغلاق مع إغلاق آخر. التواريخ شاملة للطرفين.',
    en: 'This closure overlaps another. The dates include both ends.',
  },
  COORDINATE_OUT_OF_RANGE: {
    ar: 'الإحداثيات خارج المدى: العرض ‎−90…90 والطول ‎−180…180.',
    en: 'Coordinates are out of range: latitude −90…90, longitude −180…180.',
  },
  INVALID_COORDINATE: { ar: 'الإحداثي غير صالح.', en: 'That coordinate is not valid.' },
  INVALID_SCHEDULE: { ar: 'جدول الأوقات غير صالح.', en: 'That schedule is not valid.' },
  INVALID_TIMEZONE: {
    ar: 'اختر منطقة زمنية حقيقية (مثل Asia/Damascus). الإزاحة الثابتة لا تعبّر عن التوقيت الصيفي.',
    en: 'Pick a real time zone (such as Asia/Damascus). A fixed offset cannot express daylight saving.',
  },
  INTERVALS_REQUIRED: {
    ar: 'الإغلاق بساعات معدّلة يحتاج فترات زمنية.',
    en: 'A modified-hours closure needs intervals.',
  },

  // -- media ---------------------------------------------------------------
  FILE_REQUIRED: { ar: 'اختر ملفاً.', en: 'Choose a file.' },
  MEDIA_IN_USE: {
    ar: 'هذه الصورة مستخدمة في سجل منشور. استبدلها هناك أولاً.',
    en: 'This image is used by a published record. Replace it there first.',
  },
  STORAGE_UNAVAILABLE: { ar: 'تخزين الملفات غير متاح حالياً.', en: 'File storage is unavailable right now.' },
  UNSUPPORTED_IMAGE_TYPE: {
    ar: 'الصور المقبولة: PNG أو JPEG أو WebP أو AVIF.',
    en: 'Images must be PNG, JPEG, WebP or AVIF.',
  },

  // -- settings ------------------------------------------------------------
  DUPLICATE_SOCIAL_LINK: { ar: 'هذا الرابط مضاف مسبقاً.', en: 'That link is already in the list.' },
  UNSAFE_URL: { ar: 'رابط غير آمن.', en: 'That link is not safe.' },
  HTTPS_URL_REQUIRED: { ar: 'الرابط يجب أن يبدأ بـ https.', en: 'The link has to start with https.' },
  ORIGIN_NOT_ALLOWED: { ar: 'مصدر الطلب غير مسموح.', en: 'That request origin is not allowed.' },
  ORIGIN_REQUIRED: { ar: 'مصدر الطلب مفقود.', en: 'The request origin is missing.' },

  // -- per-field codes, arriving inside error.fieldErrors ------------------
  REQUIRED: { ar: 'مطلوب', en: 'Required' },
  TOO_LONG: { ar: 'أطول من المسموح', en: 'Too long' },
  INVALID_ID: { ar: 'معرّف غير صالح', en: 'Not a valid ID' },
  INVALID_DATE: { ar: 'تاريخ غير صالح', en: 'Not a valid date' },
  INVALID_DATE_RANGE: { ar: 'مدى تواريخ غير صالح', en: 'Not a valid date range' },
  INVALID_TIME: { ar: 'وقت غير صالح', en: 'Not a valid time' },
  END_BEFORE_START: { ar: 'النهاية قبل البداية', en: 'Ends before it starts' },
  EMAIL_INVALID: { ar: 'بريد إلكتروني غير صالح', en: 'Not a valid email' },
  EMAIL_TOO_LONG: { ar: 'البريد أطول من المسموح', en: 'Email is too long' },
  PASSWORD_REQUIRED: { ar: 'كلمة المرور مطلوبة', en: 'Password is required' },
  PASSWORD_TOO_SHORT: { ar: 'أقصر من ١٢ محرفاً', en: 'Shorter than 12 characters' },
  NON_NEGATIVE_DECIMAL_REQUIRED: { ar: 'رقم موجب بخانتين عشريتين', en: 'A non-negative amount, two decimals' },

  // -- raised by the publish validator, per field --------------------------
  REQUIRED_FOR_PUBLICATION: { ar: 'مطلوب للنشر', en: 'Needed before publishing' },
  TRANSLATION_REQUIRED: { ar: 'اللغة الأخرى ناقصة', en: 'The other language is missing' },
  // No longer thrown — a small photo is accepted and upscaled. Kept because the
  // code is still a publish *warning*, and a stale sentence is worse than none.
  IMAGE_TOO_SMALL: {
    ar: 'الصورة أصغر من المقاس الموصى به.',
    en: 'The photo is smaller than recommended.',
  },
  CATEGORY_NOT_PUBLISHED: { ar: 'الفئة غير منشورة', en: 'Its category is not published' },
  TIMEZONE_NOT_CONFIGURED: { ar: 'لم تُحدَّد المنطقة الزمنية', en: 'No time zone set' },
  AT_LEAST_ONE_ITEM_REQUIRED: { ar: 'صنف واحد على الأقل', en: 'At least one item' },
  NOT_BELOW_REGULAR_PRICE: { ar: 'سعر العرض ليس أقل من السعر العادي', en: 'Not below the regular price' },
  REGULAR_PRICE_IS_ZERO: { ar: 'السعر العادي صفر، فلا خصم منه', en: 'The regular price is zero, so nothing can be taken off it' },
  PRICE_OUT_OF_RANGE: { ar: 'السعر خارج المدى المسموح', en: 'That price is out of range' },
  OUT_OF_RANGE: { ar: 'خارج المدى المسموح', en: 'Out of range' },
} satisfies Record<string, Pair>;

export type ErrorStringKey = keyof typeof errorStrings;
